import React, { useState, useRef, useEffect } from 'react';
import connectorAssistantService from '../services/connectorAssistantService';

// Extract ordered prose / ```js code segments from an assistant reply.
function parseSegments(text) {
  const segments = [];
  const re = /```(?:js|javascript)?\s*([\s\S]*?)```/gi;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: 'text', value: text.slice(last, m.index) });
    segments.push({ type: 'code', value: m[1].trim() });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', value: text.slice(last) });
  return segments;
}

// context: { name, connectorType, marketplaceName, moduleName, currentCode }
// onInsert(code): called with generated code so the parent can drop it into the editor.
const ConnectorCodeAssistant = ({ context, onInsert }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Poll a job until it's done/errored (server worker has up to 900s).
  const pollJob = async (jobId) => {
    const deadline = Date.now() + 15 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 2000));
      const res = await connectorAssistantService.result(jobId);
      const { status, reply, error } = res.data || {};
      if (status === 'done') { connectorAssistantService.remove(jobId).catch(() => {}); return reply || '(no response)'; }
      if (status === 'error') { connectorAssistantService.remove(jobId).catch(() => {}); throw new Error(error || 'The assistant failed.'); }
    }
    throw new Error('The assistant timed out.');
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setError(null);
    setLoading(true);
    try {
      const startRes = await connectorAssistantService.start(next, context);
      const jobId = startRes.data?.jobId;
      if (!jobId) throw new Error('No job id returned');
      const reply = await pollJob(jobId);
      setMessages([...next, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'The assistant request failed.');
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4a6cf7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span style={{ fontSize: '0.825rem', fontWeight: 600, color: '#111827' }}>Connector Code Assistant</span>
      </div>

      <div ref={scrollRef} style={{ overflowY: 'auto', padding: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', height: 240 }}>
        {messages.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>
            Describe what this connector should do (e.g. "poll the OHIP reservations endpoint and enqueue changed reservations").
            I'll write a ConnectorWrapper subclass — use <strong>Insert code</strong> to drop it into the editor.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '94%' }}>
            <div style={{
              fontSize: '0.8rem', lineHeight: 1.5, padding: '0.45rem 0.65rem', borderRadius: 10,
              background: m.role === 'user' ? '#eef2ff' : '#f3f4f6', color: '#111827',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {m.role === 'assistant'
                ? parseSegments(m.content).map((seg, j) => seg.type === 'text'
                    ? <span key={j}>{seg.value}</span>
                    : (
                      <div key={j} style={{ margin: '6px 0' }}>
                        <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '0.6rem', borderRadius: 8, overflowX: 'auto', fontSize: '0.72rem', margin: 0 }}>
                          <code>{seg.value}</code>
                        </pre>
                        <button
                          type="button"
                          onClick={() => onInsert(seg.value)}
                          style={{ marginTop: 4, padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, color: '#fff', background: '#4a6cf7', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                        >
                          Insert code
                        </button>
                      </div>
                    ))
                : m.content}
            </div>
          </div>
        ))}
        {loading && <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Thinking…</div>}
        {error && <div style={{ fontSize: '0.78rem', color: '#dc2626' }}>{error}</div>}
      </div>

      <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.6rem', display: 'flex', gap: 8 }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Describe the connector, or ask for a change…"
          rows={2}
          style={{ flex: 1, resize: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.5rem', fontSize: '0.8rem', fontFamily: 'inherit' }}
        />
        <button
          type="button"
          onClick={send}
          disabled={loading || !input.trim()}
          style={{ padding: '0 0.9rem', fontSize: '0.8rem', fontWeight: 600, color: '#fff', background: '#4a6cf7', border: 'none', borderRadius: 8, cursor: 'pointer', opacity: loading || !input.trim() ? 0.6 : 1 }}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ConnectorCodeAssistant;
